/* eslint-disable */
import { useState } from 'react';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import WebOutlinedIcon from '@mui/icons-material/WebOutlined';
import { urls } from '@/Constants';
import serverChatServices from '@/Services/server-chat.services';
import styles from './ServerChatPage.module.scss';

export const MAX_FILE_BYTES = 20 * 1024 * 1024;
export const MAX_FILES = 8;

export function fmtSize(bytes) {
	const b = Number(bytes) || 0;
	if (b < 1024) return `${b} B`;
	if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
	if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
	return `${(b / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

// Backend times are unix seconds; also accept ms or an ISO string, just in case.
export function toSec(t) {
	if (t === null || t === undefined || t === '') return 0;
	if (typeof t === 'number') return t > 1e12 ? Math.floor(t / 1000) : t;
	const n = Number(t);
	if (!Number.isNaN(n)) return toSec(n);
	const d = Date.parse(t);
	return Number.isNaN(d) ? 0 : Math.floor(d / 1000);
}

export function fmtWhen(t) {
	const s = toSec(t);
	return s ? new Date(s * 1000).toLocaleString() : '';
}

export function expiresLabel(expiresAt) {
	const s = toSec(expiresAt);
	if (!s) return '';
	const left = s - Math.floor(Date.now() / 1000);
	if (left <= 0) return 'expired';
	const days = Math.floor(left / 86400);
	if (days < 1) return 'expires today';
	return days === 1 ? 'expires in 1 day' : `expires in ${days} days`;
}

const KIND_LABEL = {
	xlsx: 'Excel', xls: 'Excel', csv: 'CSV', tsv: 'TSV', pdf: 'PDF', png: 'Image', jpg: 'Image', jpeg: 'Image',
	gif: 'Image', webp: 'Image', svg: 'Image', zip: 'Zip', html: 'Page', txt: 'Text', log: 'Log', json: 'JSON',
	md: 'Text', docx: 'Word', doc: 'Word', pptx: 'PowerPoint',
};

export function kindOf(name, kind) {
	return String(kind || (name || '').split('.').pop() || '').toLowerCase();
}

export function kindLabel(name, kind) {
	const k = kindOf(name, kind);
	return KIND_LABEL[k] || (k ? k.toUpperCase() : 'File');
}

export function KindIcon({ name, kind }) {
	const k = kindOf(name, kind);
	if (['xlsx', 'xls', 'csv', 'tsv'].includes(k)) return <TableChartOutlinedIcon fontSize='inherit' />;
	if (k === 'pdf') return <PictureAsPdfOutlinedIcon fontSize='inherit' />;
	if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(k)) return <ImageOutlinedIcon fontSize='inherit' />;
	if (k === 'html') return <WebOutlinedIcon fontSize='inherit' />;
	return <InsertDriveFileOutlinedIcon fontSize='inherit' />;
}

// Link to the full-page view of a live page (same base as the sidebar links).
export function pageHref(pageId) {
	return `${window.location.origin}${urls.EXTENSION}${urls.SERVER_CHAT}/page/${pageId}`;
}

// One downloadable file tile (user upload or a file Claude made).
export function FileChip({ chatId, area, name, shown, size, kind, onError, shared }) {
	const [busy, setBusy] = useState(false);
	const label = shown || name;
	const download = () => {
		if (busy || !chatId) return;
		setBusy(true);
		serverChatServices.downloadFile(chatId, area, name, label, shared)
			.catch((e) => onError && onError(String(e || 'Download failed')))
			.finally(() => setBusy(false));
	};
	return (
		<button type='button' className={styles.fileTile} onClick={download} disabled={busy} title={`Download ${label}`}>
			<span className={styles.fileIcon}><KindIcon name={label} kind={kind} /></span>
			<span className={styles.fileText}>
				<span className={styles.fileName}>{label}</span>
				<span className={styles.fileMeta}>{kindLabel(label, kind)}{size ? ` · ${fmtSize(size)}` : ''}</span>
			</span>
			<span className={styles.fileAct}>{busy ? <span className={styles.spin} /> : <FileDownloadOutlinedIcon fontSize='inherit' />}</span>
		</button>
	);
}

// Card for a live page Claude made: open it in a new tab, or download the HTML.
export function PageCard({ chatId, page, onError, shared }) {
	const [busy, setBusy] = useState(false);
	const exp = expiresLabel(page.expiresAt);
	const expired = exp === 'expired';
	const download = () => {
		if (busy || !chatId || !page.name) return;
		setBusy(true);
		serverChatServices.downloadFile(chatId, 'out', page.name, page.name, shared)
			.catch((e) => onError && onError(String(e || 'Download failed')))
			.finally(() => setBusy(false));
	};
	return (
		<div className={styles.pageCard}>
			<span className={styles.fileIcon}><WebOutlinedIcon fontSize='inherit' /></span>
			<div className={styles.fileText}>
				<span className={styles.fileName}>{page.title || page.name || 'Page'}</span>
				<span className={styles.fileMeta}>
					Page{page.version ? ` · v${page.version}` : ''}{exp ? ` · ${exp}` : ''}
				</span>
			</div>
			<div className={styles.pageActs}>
				{!expired && (
					<a className={styles.btnGhost} href={pageHref(page.pageId)} target='_blank' rel='noopener noreferrer'>
						Open page <OpenInNewRoundedIcon fontSize='inherit' />
					</a>
				)}
				{!!page.name && (
					<button type='button' className={styles.btnGhost} onClick={download} disabled={busy} title='Download HTML'>
						{busy ? <span className={styles.spin} /> : <FileDownloadOutlinedIcon fontSize='inherit' />} Download
					</button>
				)}
			</div>
		</div>
	);
}
